/**
 * The teacher's profile: where they teach, what, and to whom. Pure.
 *
 * These three fields are not ours — they are the Learning Commons Knowledge Graph's
 * `jurisdiction`, `academicSubject`, and `gradeLevel` filters, reproduced exactly. Keeping
 * them identical is the point: the profile is the query, and a value the API will reject
 * must never reach it.
 */

export const JURISDICTIONS = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming', 'Washington, D.C.', 'Multi-State',
] as const;

export const ACADEMIC_SUBJECTS = [
  'English Language Arts',
  'Mathematics',
  'Science',
  'Social Studies',
  'Other',
] as const;

export const GRADE_LEVELS = [
  'PK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12',
] as const;

export type Jurisdiction = (typeof JURISDICTIONS)[number];
export type AcademicSubject = (typeof ACADEMIC_SUBJECTS)[number];
export type GradeLevel = (typeof GRADE_LEVELS)[number];

export interface Profile {
  jurisdiction: Jurisdiction;
  academicSubject?: AcademicSubject;
  gradeLevels: GradeLevel[];
}

export type ProfileField = 'body' | 'jurisdiction' | 'academicSubject' | 'gradeLevels';

export type ProfileDecision =
  | { ok: true; profile: Profile }
  | { ok: false; field: ProfileField; reason: string };

const reject = (field: ProfileField, reason: string): ProfileDecision =>
  ({ ok: false, field, reason });

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function decideProfile(raw: unknown): ProfileDecision {
  if (!isPlainObject(raw)) {
    return reject('body', 'Expected an object with jurisdiction, academicSubject and gradeLevels.');
  }

  const jurisdiction = raw.jurisdiction;
  if (!JURISDICTIONS.includes(jurisdiction as Jurisdiction)) {
    return reject('jurisdiction', 'Pick a state, Washington, D.C., or Multi-State.');
  }

  const academicSubject = raw.academicSubject === '' || raw.academicSubject === null ? undefined : raw.academicSubject;
  if (academicSubject !== undefined && !ACADEMIC_SUBJECTS.includes(academicSubject as AcademicSubject)) {
    return reject('academicSubject', `Pick one of: ${ACADEMIC_SUBJECTS.join(', ')}.`);
  }

  const gradeLevels = raw.gradeLevels ?? [];
  if (!Array.isArray(gradeLevels)) {
    return reject('gradeLevels', 'Grade levels must be a list.');
  }
  if (!gradeLevels.every((g) => GRADE_LEVELS.includes(g as GradeLevel))) {
    return reject('gradeLevels', `Grade levels are ${GRADE_LEVELS.join(', ')}.`);
  }

  // Sorted into enum order and de-duplicated, so two profiles that mean the same thing are
  // the same row and produce the same query string.
  const unique = GRADE_LEVELS.filter((g) => (gradeLevels as GradeLevel[]).includes(g));

  return {
    ok: true,
    profile: {
      jurisdiction: jurisdiction as Jurisdiction,
      ...(academicSubject ? { academicSubject: academicSubject as AcademicSubject } : {}),
      gradeLevels: [...unique],
    },
  };
}
